import { computed, toRef, type Ref, type ComputedRef } from 'vue'
import type {
    CatalogueSortOrder,
    CatalogueWeddingRingFilterInput,
    MetalCode,
    WeddingRingCategoryLabelCode,
    WeddingRingFiltersFragment,
    WeddingRingStyleLabelCode,
} from '@/graphql/api'
import { useRoutes, type WeddingRingsRoute } from '@/router/routes'
import { useCountryPlugin } from '@/plugins/country.ts'
import type { WeddingRingsProps } from './WeddingRings.vue'
import { useFiltersCount } from '../useFiltersCount'
import { useSearch } from '../composables/useSearch'
import type { FilterChip } from '@/components/ui/filter/FilterChips.vue'
import { useFormattedPriceRange } from '@/composables/useMoneyFormatter'
import { systemCodeToWords } from '@/helpers'
import type { InputRangeModel } from '@/components/ui/inputs/range/input-range-two-knobbed-props.ts'
import { useCurrencyInput } from '@/plugins/currency-input.ts'

interface WeddingRingsFilters {
    filterRoutes: {
        routeReset: WeddingRingsRoute
        getRoutePage(page: number): WeddingRingsRoute
        getRoutePrice(values: InputRangeModel): WeddingRingsRoute
        getRouteAddMetal(metal: MetalCode): WeddingRingsRoute
        getRouteRemoveMetal(): WeddingRingsRoute
        getRouteAddRingStyle(
            ringStyle: WeddingRingStyleLabelCode
        ): WeddingRingsRoute
        getRouteRemoveRingStyle(): WeddingRingsRoute
        getRouteAddCategory(
            category: WeddingRingCategoryLabelCode
        ): WeddingRingsRoute
        getRouteRemoveCategory(): WeddingRingsRoute
        getRouteSort(sortBy: CatalogueSortOrder): WeddingRingsRoute
    }
    filterInput: ComputedRef<CatalogueWeddingRingFilterInput>
    activeFilterCount: ComputedRef<number>
    filterChips: ComputedRef<FilterChip[]>
    search: Ref<string>
}

export function useWeddingRingsFilters(
    props: WeddingRingsProps,
    filters: Readonly<Ref<WeddingRingFiltersFragment | undefined>>
): WeddingRingsFilters {
    const { countryCode } = useCountryPlugin()
    const currency = useCurrencyInput()
    const routes = useRoutes()

    const getRoute = (
        filter: CatalogueWeddingRingFilterInput,
        page?: number,
        sortBy?: CatalogueSortOrder
    ): WeddingRingsRoute => {
        const metal = filter.primaryMetalCodes?.[0]
        const category = filter.categories?.[0]
        const ringStyle = filter.ringStyles?.[0]
        const priceMin = filter.price?.min
        const priceMax = filter.price?.max

        return routes.weddingRings({
            category,
            metal,
            ringStyle,
            priceMax: priceMax ? Number(priceMax) : undefined,
            priceMin: priceMin ? Number(priceMin) : undefined,
            page,
            sortBy: sortBy || props.sortBy,
        })
    }

    const filterRoutes = {
        routeReset: routes.weddingRings({}),
        getRoutePage: (page: number) => getRoute(filterInput.value, page),

        getRoutePrice: (values: InputRangeModel) =>
            getRoute({
                ...filterInput.value,
                price: {
                    country: countryCode.value!,
                    currency: currency.value,
                    min: String(values[0]),
                    max: String(values[1]),
                },
            }),

        getRouteAddMetal: (metal: MetalCode) =>
            getRoute(
                {
                    ...filterInput.value,
                    primaryMetalCodes: [metal],
                },
                1
            ),

        getRouteRemoveMetal: () =>
            getRoute(
                {
                    ...filterInput.value,
                    primaryMetalCodes: undefined,
                },
                1
            ),

        getRouteAddRingStyle: (ringStyle: WeddingRingStyleLabelCode) =>
            getRoute(
                {
                    ...filterInput.value,
                    ringStyles: [ringStyle],
                },
                1
            ),

        getRouteRemoveRingStyle: () =>
            getRoute(
                {
                    ...filterInput.value,
                    ringStyles: undefined,
                },
                1
            ),

        getRouteAddCategory: (category: WeddingRingCategoryLabelCode) =>
            getRoute(
                {
                    ...filterInput.value,
                    categories: [category],
                },
                1
            ),

        getRouteRemoveCategory: () =>
            getRoute(
                {
                    ...filterInput.value,
                    categories: undefined,
                },
                1
            ),

        getRouteSort: (sortBy: CatalogueSortOrder) =>
            getRoute(filterInput.value, 1, sortBy),
    }

    const { search } = useSearch(
        toRef(() => props.page),
        filterRoutes.getRoutePage
    )

    const filterInput = computed(
        (): CatalogueWeddingRingFilterInput => ({
            categories: props.category ? [props.category] : [],
            ringStyles: props.ringStyle ? [props.ringStyle] : [],
            primaryMetalCodes: props.metal ? [props.metal] : [],
            price: {
                country: countryCode.value!,
                currency: currency.value,
                min: props.priceMin ? String(props.priceMin) : null,
                max: props.priceMax ? String(props.priceMax) : null,
            },
            search: search.value !== '' ? search.value : null,
        })
    )

    const activeFilterCount = useFiltersCount(
        computed<boolean[]>(() => [
            !!filterInput.value.categories?.length,
            !!filterInput.value.primaryMetalCodes?.length,
            !!filterInput.value.ringStyles?.length,
            !!(filterInput.value.price?.min || filterInput.value.price?.max),
        ])
    )

    const formattedPriceRange = useFormattedPriceRange(
        computed(() => ({
            min: { amount: props.priceMin },
            max: { amount: props.priceMax },
        }))
    )

    const filterChips = computed((): FilterChip[] => {
        const chips: FilterChip[] = []

        if (formattedPriceRange.value) {
            chips.push({
                label: formattedPriceRange.value,
                getRouteRemove: () =>
                    getRoute({
                        ...filterInput.value,
                        price: undefined,
                    }),
            })
        }

        const category = filterInput.value.categories?.[0]
        if (category) {
            chips.push({
                label:
                    filters.value?.categories.find(
                        (c) => c.label.systemCode === category
                    )?.label.translation.name || systemCodeToWords(category),
                getRouteRemove: filterRoutes.getRouteRemoveCategory,
            })
        }

        const primaryMetalCode = filterInput.value.primaryMetalCodes?.[0]
        if (primaryMetalCode) {
            chips.push({
                label:
                    filters.value?.primaryMetals.find(
                        (m) => m.metal.systemCode === primaryMetalCode
                    )?.metal.translation.name ||
                    systemCodeToWords(primaryMetalCode),
                getRouteRemove: filterRoutes.getRouteRemoveMetal,
            })
        }

        const ringStyle = filterInput.value.ringStyles?.[0]
        if (ringStyle) {
            chips.push({
                label:
                    filters.value?.ringStyles.find(
                        (s) => s.label.systemCode === ringStyle
                    )?.label.translation.name || systemCodeToWords(ringStyle),
                getRouteRemove: filterRoutes.getRouteRemoveRingStyle,
            })
        }

        return chips
    })

    return {
        filterRoutes,
        filterInput,
        activeFilterCount,
        filterChips,
        search,
    }
}
